/*******************************************************
 * JobContext Provider
 * 
 * Provides global access to table data throughout the application.
 * Handles fetching and caching of data from the Job database.
 * Also manages the active job ID state.
 * 
 * Key Features:
 * - Centralized data management
 * - Automatic data fetching with loading states
 * - Type-safe access to tables
 * - Error handling and reporting
 * - Active job tracking
 ********************************************************/

//########## IMPORTS ##########
// #region IMPORTS
'use client';
// External dependencies
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { Alert, Box, Typography, Paper, Container, Button } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import Image from 'next/image';
// Internal utilities and types
import { type TableConfig, type SchemaTableName, type APIGetSuccessResponse } from '@/src/scripts/libs/typesLib';
import { logout } from '@/src/scripts/libs/clientUtilLib';
import { type TableModel } from '../db/models/EntityRelationships.types';
import { getDesignTokens } from '@/src/theme/theme';
import TRUELogo from '@/public/assets/media/Logos/TRUE2 Logo.png';
import { isContextReferenceStale } from './utils/contextRefreshPolicy';
// #endregion


//########## TYPE DEFINITIONS ##########
//#region TYPE DEFINITIONS


// Type for table names from the TABLES constant
type TableKeys = keyof typeof TABLES;

// Maps table names to their corresponding data types from the schema
type TableTypes = {
    [K in TableKeys]: TableModel<K, []>;
};

// Main context type definition
interface JobContextType {
    Job: {                                          // Contains data for all tables
        [K in TableKeys]: APIGetSuccessResponse<TableTypes[K]>
    };
    loading: boolean;                               // Global loading state
    ready: boolean;                                 // True after initial load completes when ActiveJobID exists
    error: string | null;                           // Global error state
    refreshReferencesIfStale: () => Promise<void>;  // Refresh Jobs after 30 minutes
    ActiveJobID: number | null;                     // Currently selected job ID
    setActiveJobID: (id: number | null) => void;    // Function to update active job
}
//#endregion


//########## CONSTANTS & Global Variables ##########
//#region CONSTANTS
// Map of all available tables and their API endpoints
const TABLES: Partial<Record<SchemaTableName, TableConfig>> = {
    Jobs: {
        path: '/api/v1/AppDB/Jobs',
        params: { ID__GT: '1111' }
    }
} as const;

// Create the context with undefined default value
const JobContext = createContext<JobContextType | undefined>(undefined);

// Light theme for the "No Jobs" page — matches the login page appearance
const noJobsTheme = createTheme(getDesignTokens('light'));
//#endregion


//########## FUNCTIONS ##########
// #region FUNCTIONS
// Builds complete API URL from table configuration
function constructEndpoint(config: TableConfig): string {
    const url = new URL(config.path, window.location.origin);
    if (config.params) {
        const searchParams = config.params instanceof URLSearchParams
            ? config.params
            : new URLSearchParams(config.params);
        url.search = searchParams.toString();
    }
    return url.toString();
}

// Hook to access the JobContext from any component
export function useJob() {
    const context = useContext(JobContext);
    if (context === undefined) {
        throw new Error('useJobContext must be used within a JobContextProvider');
    }
    return context;
}
//#endregion

export function JobContextProvider({ children, jobs, defaultJobID }: { children: ReactNode, jobs: { ID: number }[], defaultJobID: number | null }) {
    //########## MAIN LOGIC ##########
    // #region MAIN LOGIC
    // Initialize state for all tables with empty data
    const [Job, setTables] = useState<JobContextType['Job']>(
        Object.keys(TABLES).reduce((acc, key) => ({
            ...acc,
            [key]: {
                data: [],
                requestURL: '',
                page: 0,
                pageSize: 0,
                recordsCount: 0
            }
        }), {} as JobContextType['Job'])
    );
    const [loading, setLoading] = useState(false);    // Loading state
    const [ready, setReady] = useState(false);        // Initial-load readiness
    const [error, setError] = useState<string | null>(null);  // Error state
    const [activeJobInitialized, setActiveJobInitialized] = useState(false);
    const lastFetchedAtRef = useRef(0);
    const fetchInFlightRef = useRef<Promise<void> | null>(null);
    // Keep initial render SSR-safe. localStorage reconciliation runs after mount.
    const [ActiveJobID, setActiveJobID] = useState<number | null>(null);

    // Reconcile with localStorage after hydration.
    // If an admin removes a job from a user, localStorage may still hold an old ID.
    // Using that stale ID would cause PGContext to request an invalid DPKey and trigger auth failures.
    useEffect(() => {
        const validJobs = jobs.filter(j => j.ID != null);
        const stored = localStorage.getItem('ActiveJobID');
        const parsedStoredID = stored ? parseInt(stored, 10) : NaN;
        const storedID = Number.isFinite(parsedStoredID) ? parsedStoredID : null;

        const nextActiveJobID =
            storedID != null && validJobs.some(j => j.ID === storedID)
                ? storedID
                : defaultJobID != null && validJobs.some(j => j.ID === defaultJobID)
                    ? defaultJobID
                    : (validJobs.length > 0 ? validJobs[0].ID : null);

        setActiveJobID(prev => prev === nextActiveJobID ? prev : nextActiveJobID);

        if (nextActiveJobID) {
            localStorage.setItem('ActiveJobID', nextActiveJobID.toString());
        } else {
            localStorage.removeItem('ActiveJobID');
            setReady(false);
        }
        setActiveJobInitialized(true);
    }, [jobs, defaultJobID]);

    // Keep localStorage in sync when user changes active job client-side.
    useEffect(() => {
        if (!activeJobInitialized) return;

        if (ActiveJobID) {
            localStorage.setItem('ActiveJobID', ActiveJobID.toString());
        } else {
            localStorage.removeItem('ActiveJobID');
        }
    }, [ActiveJobID, activeJobInitialized]);

    // Fetches data for all tables
    const fetchTables = () => {
        if (fetchInFlightRef.current) return fetchInFlightRef.current;

        const request = (async () => {
            try {
                setLoading(true);
                setError(null);

                // Fetch all tables in parallel
                const responses = await Promise.all(
                    Object.entries(TABLES).map(([key, config]) =>
                        fetch(constructEndpoint(config))
                            .then(async res => {
                                if (!res.ok) throw new Error(`Failed to fetch ${key}: ${res.statusText}`);
                                return res.json() as Promise<APIGetSuccessResponse<TableTypes[Extract<typeof key, TableKeys>]>>;
                            })
                            .then(json => ({ key, response: json }))
                    )
                );

                // Update state with fetched data
                setTables(
                    responses.reduce((acc, { key, response }) => ({
                        ...acc,
                        [key]: response
                    }), {} as JobContextType['Job'])
                );
                lastFetchedAtRef.current = Date.now();
            } catch (err) {
                setError(err instanceof Error ? err.message : 'An error occurred');
            } finally {
                setLoading(false);
                fetchInFlightRef.current = null;
            }
        })();

        fetchInFlightRef.current = request;
        return request;
    };

    const refreshReferencesIfStale = async () => {
        if (isContextReferenceStale(lastFetchedAtRef.current)) {
            await fetchTables();
        }
    };

    useEffect(() => {
        if (!activeJobInitialized) return;

        if (ActiveJobID && lastFetchedAtRef.current === 0) {
            void fetchTables();
        } else if (!ActiveJobID) {
            setLoading(false);
        }
    }, [ActiveJobID, activeJobInitialized]); // Load Jobs once after the initial active job is resolved

    // Mark ready after first successful fetch for a given ActiveJobID
    useEffect(() => {
        if (!loading && ActiveJobID && !error && !ready) {
            setReady(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, ActiveJobID, error]);

    //#endregion


    //########## MARKDOWN ##########
    // #region MARKDOWN
    if (activeJobInitialized && !ActiveJobID) {
        return (
            <ThemeProvider theme={noJobsTheme}>
                <Box sx={{
                    minHeight: '100vh', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', bgcolor: 'background.default',
                }}>
                    <Container component="main" sx={{ maxWidth: '444px', display: 'flex', alignItems: 'center' }}>
                        <Paper elevation={24} sx={{
                            p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center',
                            width: '100%', borderRadius: 2,
                        }}>
                            <Box sx={{ width: '240px', height: '100px', position: 'relative', mb: 2 }}>
                                <Image src={TRUELogo} alt="TRUE Logo" sizes="240px" fill style={{ objectFit: 'contain' }} priority />
                            </Box>

                            <Typography variant="h4" sx={{ fontWeight: 700, textAlign: 'center', mb: 1 }}>
                                No Assigned Jobs
                            </Typography>
                            <Typography variant="body1" sx={{ color: 'text.secondary', textAlign: 'center', mb: 3 }}>
                                Your account does not have any jobs assigned to it.
                            </Typography>

                            <Alert severity="warning" sx={{ width: '100%', mb: 1.5 }}>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                    Please contact your Field Controls person, Field Timekeeper, or the Field Solutions team to have a job assigned to your account.
                                </Typography>
                            </Alert>

                            <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center', mb: 1 }}>
                                Once a job has been assigned, log in again to continue.
                            </Typography>
                            <Button
                                variant="contained" color="primary" size="large"
                                sx={{ width: '80%' }}
                                onClick={() => logout()}
                            >
                                Back to Login
                            </Button>
                        </Paper>
                    </Container>
                </Box>
            </ThemeProvider>
        );
    }
    // Provide context values to children components
    return (
        <JobContext.Provider value={{
            Job,
            loading,
            ready,
            error,
            refreshReferencesIfStale,
            ActiveJobID,
            setActiveJobID
        }}>
            {children}
        </JobContext.Provider>
    );
    //#endregion
} 
